import { z } from "zod"
import { TranslationKey } from "../i18n/translation-key"
import { JobStatus } from "../service/job"

export enum DeliveryStatus {
  TODO = "TODO",
  WAITING = "WAITING",
  DELIVERED = "DELIVERED",
}

export const DeliveryStatusSchema = z.nativeEnum(DeliveryStatus)

export interface DeliveryExpectedDateChange {
  oldDate: Date
  newDate: Date
  changedAt: Date
  changedBy?: { id: string; firstName: string }
}

export interface Delivery {
  id: string
  amount: number
  description?: string
  supplierId?: string
  status: DeliveryStatus
  priority: number
  storeId: string
  createdById: string
  assignedMechanicId?: string
  deadlineToOrderAt?: Date
  expectedDeliveryAt?: Date
  /** Audit trail of expectedDeliveryAt changes, ordered by `changedAt` ascending
   *  (i.e. the order they were set). */
  expectedDeliveryHistory?: DeliveryExpectedDateChange[]
  orderedAt?: Date
  orderedById?: string
  deliveredAt?: Date
  deliveredById?: string
  jobId?: string
  deliveryProductId: string
  createdAt: Date
  modifiedAt: Date
  timeDifferenceFromDeadline?: number

  createdBy?: {
    id: string
    firstName: string
    lastName: string
  }
  orderedBy?: {
    id: string
    firstName: string
    lastName: string
  }
  deliveredBy?: {
    id: string
    firstName: string
    lastName: string
  }
  assignedMechanic?: {
    id: string
    name: string
  }
  deliveryProduct?: {
    id: string
    title: string
    ean: string | null
  }
  supplier?: {
    id: string
    name: string
    alias: string[]
    logo: string | null
  }
  job?: {
    id: string
    status: JobStatus
    physicalTag?: number
    customer?: {
      id: string
      firstName: string
      lastName: string | null
    }
    bike?: {
      brand: { id: string; name: string } | null
      color: string | null
    }
    isBooking: boolean
  }
}

export const CreateDeliveryRequestSchema = z
  .object({
    deliveryProductId: z
      .string()
      .uuid(
        "common.validation.deliveryProductRequired" satisfies TranslationKey,
      ),
    amount: z.number().int().positive().optional().default(1),
    description: z.string().optional(),
    supplierId: z.string().uuid().optional(),
    assignedMechanicId: z.string().uuid().optional(),
    status: DeliveryStatusSchema.optional().default(DeliveryStatus.TODO),
    deadlineToOrderAt: z.coerce.date().optional(),
    expectedDeliveryAt: z.coerce.date().optional(),
    jobId: z.string().uuid().optional(),
  })
  .refine(
    (data) => {
      // If status is TODO (or defaults to TODO) AND jobId is present,
      // expectedDeliveryAt must be provided
      const status = data.status ?? DeliveryStatus.TODO
      if (status === DeliveryStatus.TODO && data.jobId != null) {
        return data.expectedDeliveryAt != null
      }
      return true
    },
    {
      message:
        "common.validation.expectedDeliveryDateRequired" satisfies TranslationKey,
      path: ["expectedDeliveryAt"],
    },
  )
export const UpdateDeliveryRequestSchema = z
  .object({
    deliveryProductId: z.string().uuid().optional(),
    amount: z.number().int().positive().optional(),
    description: z.string().optional(),
    supplierId: z.string().uuid().optional().nullable(),
    status: DeliveryStatusSchema.optional(),
    priority: z.number().optional(),
    assignedMechanicId: z.string().uuid().optional().nullable(),
    deadlineToOrderAt: z.coerce.date().optional().nullable(),
    expectedDeliveryAt: z.coerce.date().optional().nullable(),
    deliveredAt: z.coerce.date().optional().nullable(),
    jobId: z.string().uuid().optional().nullable(),
  })
  .refine(
    (data) => {
      // When updating to TODO status with a jobId, require expectedDeliveryAt
      if (data.status === DeliveryStatus.TODO && data.jobId != null) {
        return data.expectedDeliveryAt != null
      }
      return true
    },
    {
      message:
        "common.validation.expectedDeliveryDateRequired" satisfies TranslationKey,
      path: ["expectedDeliveryAt"],
    },
  )

export const UpdateDeliveryPositionRequestSchema = z.object({
  deliveryId: z.string().uuid("Invalid deliveryId format"),
  /** When provided, also updates the active calendar date field
   *  (deadlineToOrderAt for TODO, expectedDeliveryAt for WAITING/DELIVERED).
   *  Omit to update priority only. */
  date: z
    .string()
    .datetime({ message: "Invalid ISO 8601 datetime format" })
    .optional(),
  priority: z.number(),
})

export interface UpdateDeliveryPositionRequest {
  deliveryId: string
  date?: string
  priority: number
}

export type CreateDeliveryRequest = z.infer<typeof CreateDeliveryRequestSchema>
export type UpdateDeliveryRequest = z.infer<typeof UpdateDeliveryRequestSchema>
